import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import {
  useCompanies,
  useJobTitles,
  useRoles,
} from "../../api/queries/organizationQueries";
import {
  useActivateTemplate,
  useCreateTemplate,
  useNewTemplateVersion,
  useTemplate,
  useUpdateTemplate,
} from "../../api/queries/performanceQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";
import { formatDateTime } from "../../utils/dateTime";
import TemplateStatusBadge from "./components/TemplateStatusBadge";
import GeneralCard from "./components/builder/GeneralCard";
import SectionsEditor from "./components/builder/SectionsEditor";
import RatingBandsEditor from "./components/builder/RatingBandsEditor";
import TemplatePreview from "./components/builder/TemplatePreview";
import {
  emptyTemplate,
  fromServer,
  markSaved,
  toPayload,
} from "./components/templateUtils";
import "./performance.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];
const byLabel = (a, b) => a.label.localeCompare(b.label);

/** Template validation errors come back as an array of readable problems. */
const errorList = (err, fallback) => {
  const msg = err?.response?.data?.message;
  return Array.isArray(msg) ? msg : [getApiErrorMessage(err, fallback)];
};

/**
 * Template builder.
 * /performance/templates/new          → empty draft (created on first save)
 * /performance/templates/:id[/edit]   → editable when DRAFT, read-only otherwise
 */
const TemplateBuilderPage = () => {
  const { id } = useParams();
  const canModify = useCanModify("performance", { company: true });
  const navigate = useNavigate();
  // HR-level (performance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("performance");
  const isCreate = !id;

  const query = useTemplate(id);
  const { data: companies = EMPTY } = useCompanies();
  const { data: roles = EMPTY } = useRoles();
  const { data: jobTitles = EMPTY } = useJobTitles();

  const roleOptions = useMemo(
    () =>
      roles
        .map((r) => ({
          value: r.id,
          label: r.title || r.name || "Unnamed role",
        }))
        .sort(byLabel),
    [roles],
  );
  const jobTitleOptions = useMemo(
    () =>
      jobTitles
        .map((j) => ({ value: j.id, label: j.title || j.name || "Unnamed" }))
        .sort(byLabel),
    [jobTitles],
  );

  // Builder state; loaded once per template id (saving keeps the local state)
  const [form, setForm] = useState(() => (isCreate ? emptyTemplate() : null));
  const [loadedId, setLoadedId] = useState(isCreate ? "new" : null);
  const [snapshot, setSnapshot] = useState(() =>
    isCreate
      ? JSON.stringify(toPayload(emptyTemplate(), { isCreate: true }))
      : null,
  );
  // The same page instance is reused between /new and /:id, so re-sync on id change
  if (isCreate && loadedId !== "new") {
    const blank = emptyTemplate();
    setForm(blank);
    setLoadedId("new");
    setSnapshot(JSON.stringify(toPayload(blank, { isCreate: true })));
  } else if (!isCreate && query.data?.id === id && loadedId !== id) {
    const loaded = fromServer(query.data);
    setForm(loaded);
    setLoadedId(id);
    setSnapshot(JSON.stringify(toPayload(loaded)));
  }
  const ready = !!form && loadedId === (isCreate ? "new" : id);

  const [errors, setErrors] = useState([]);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const errorRef = useRef(null);

  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const activate = useActivateTemplate();
  const newVersion = useNewTemplateVersion();
  const busy =
    create.isPending ||
    update.isPending ||
    activate.isPending ||
    newVersion.isPending;

  // Read-only when not a draft, or when the role can view performance but not change it
  const readOnly =
    ready && (!canModify || (!isCreate && form.status !== "DRAFT"));
  const dirty =
    ready &&
    !readOnly &&
    JSON.stringify(toPayload(form, { isCreate })) !== snapshot;

  // Warn before closing the tab with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const showErrors = (list) => {
    setErrors(list);
    requestAnimationFrame(() =>
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  const handleError = (err, fallback) => {
    const list = errorList(err, fallback);
    if (getApiErrorStatus(err) === 409) toast.info(list.join(" "));
    showErrors(list);
  };

  const patchForm = (changes) => setForm((f) => ({ ...f, ...changes }));

  /** Save the draft; resolves to the template id, or null on failure. */
  const saveDraft = async () => {
    const problems = [];
    if (isCreate && !form.code.trim()) problems.push("Enter a code.");
    if (!form.name.trim()) problems.push("Enter a name.");
    if (problems.length) {
      showErrors(problems);
      return null;
    }
    setErrors([]);
    const body = toPayload(form, { isCreate });
    try {
      if (isCreate) {
        const created = await create.mutateAsync(body);
        toast.success("Draft created.");
        navigate(`/performance/templates/${created.id}/edit`, {
          replace: true,
        });
        return created.id;
      }
      await update.mutateAsync({ id, body });
      setForm((f) => markSaved(f)); // keys are locked from now on
      setSnapshot(JSON.stringify(toPayload(form)));
      toast.success("Draft saved.");
      return id;
    } catch (err) {
      handleError(err, "Could not save the template.");
      return null;
    }
  };

  const doActivate = async () => {
    setConfirmActivate(false);
    const savedId = isCreate || dirty ? await saveDraft() : id;
    if (!savedId) return;
    try {
      await activate.mutateAsync(savedId);
      toast.success(`${form.name || "Template"} is now active.`);
      navigate("/performance/templates");
    } catch (err) {
      handleError(err, "Could not activate the template.");
    }
  };

  const startNewVersion = () =>
    newVersion.mutate(id, {
      onSuccess: (draft) => {
        toast.success(`Draft v${draft.version} created.`);
        navigate(`/performance/templates/${draft.id}/edit`);
      },
      onError: (err) => handleError(err, "Could not create a new version."),
    });

  const cancel = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    navigate("/performance/templates");
  };

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="builder-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can manage
            evaluation templates.
          </div>
        </div>
      </Layout>
    );
  }

  if (!ready) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          {query.isError ? (
            <div className="alert alert-danger" role="alert">
              {getApiErrorMessage(query.error, "Could not load the template.")}{" "}
              <Link to="/performance/templates">Back to templates</Link>
            </div>
          ) : (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        {/* Header + actions */}
        <div className="builder-header">
          <div className="builder-header-text">
            <Link
              to="/performance/templates"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Evaluation templates
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title text-truncate">
              {isCreate ? "New template" : form.name || form.code}
            </h1>
            {!isCreate && (
              <div className="d-flex flex-wrap align-items-center gap-2 fs-080 text-slate-500 mt-1">
                <span className="font-monospace">{form.code}</span>
                <span>v{form.version}</span>
                <TemplateStatusBadge status={form.status} />
                {form.updatedAt && (
                  <span>Updated {formatDateTime(form.updatedAt)}</span>
                )}
                {dirty && (
                  <span className="text-warning-emphasis fw-semibold">
                    · Unsaved changes
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {readOnly ? (
              <button
                type="button"
                className="btn btn-primary profile-btn"
                onClick={startNewVersion}
                disabled={busy}
              >
                {newVersion.isPending && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                <i className="bi bi-files"></i>{" "}
                {form.status === "ARCHIVED"
                  ? "New version from this"
                  : "New version"}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn profile-btn profile-btn-white"
                  onClick={cancel}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary profile-btn bg-white"
                  onClick={saveDraft}
                  disabled={busy || (!dirty && !isCreate)}
                >
                  {(create.isPending || update.isPending) && (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  )}
                  <i className="bi bi-save"></i> Save draft
                </button>
                <button
                  type="button"
                  className="btn btn-success profile-btn"
                  onClick={() => setConfirmActivate(true)}
                  disabled={busy}
                >
                  {activate.isPending && (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  )}
                  <i className="bi bi-check2-circle"></i> Activate
                </button>
              </>
            )}
          </div>
        </div>

        <div ref={errorRef}>
          {errors.length > 0 && (
            <div className="alert alert-danger fs-085" role="alert">
              <div className="fw-semibold mb-1">
                <i className="bi bi-exclamation-octagon me-1"></i>
                {errors.length === 1
                  ? "Please fix this:"
                  : `Please fix these ${errors.length} problems:`}
              </div>
              <ul className="mb-0 ps-3">
                {errors.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}
          {readOnly && (
            <div className="alert alert-info fs-085 d-flex gap-2" role="status">
              <i className="bi bi-lock mt-1"></i>
              <div>
                This version is{" "}
                <strong>
                  {form.status === "ACTIVE" ? "active" : "archived"}
                </strong>{" "}
                and can't be edited. Create a new version to make changes;
                evaluations already started keep their form.
              </div>
            </div>
          )}
        </div>

        <div className="builder-layout">
          <fieldset disabled={readOnly || busy} className="builder-main">
            <GeneralCard
              form={form}
              update={patchForm}
              isCreate={isCreate}
              readOnly={readOnly}
              companies={companies}
              roleOptions={roleOptions}
              jobTitleOptions={jobTitleOptions}
            />
            <SectionsEditor
              sections={form.sections}
              onChange={(sections) => patchForm({ sections })}
              readOnly={readOnly}
            />
            <RatingBandsEditor
              bands={form.ratingBands}
              onChange={(ratingBands) => patchForm({ ratingBands })}
              readOnly={readOnly}
            />
          </fieldset>
          <TemplatePreview form={form} />
        </div>
      </div>

      {confirmActivate && (
        <Dialog
          title="Activate this template?"
          onClose={() => setConfirmActivate(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setConfirmActivate(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success profile-btn"
                onClick={doActivate}
              >
                {dirty || isCreate ? "Save & activate" : "Activate"}
              </button>
            </>
          }
        >
          <p className="fs-085 mb-2">
            Activating replaces the current active version for new evaluations.
            Existing evaluations keep their form.
          </p>
          {(dirty || isCreate) && (
            <p className="fs-080 text-slate-500 mb-0">
              Your unsaved changes are saved first.
            </p>
          )}
        </Dialog>
      )}
    </Layout>
  );
};

export default TemplateBuilderPage;
